'use client';

import Icon from '@/components/ui/AppIcon';

interface GuestRightsSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function GuestRightsSection({ isExpanded, onToggle }: GuestRightsSectionProps) {
  const rights = [
    {
      icon: 'EyeIcon',
      title: 'Right to Access',
      description: 'Request a copy of all personal data we hold about you',
      action: 'Request Data Copy',
      timeline: 'Within 30 days'
    },
    {
      icon: 'PencilIcon',
      title: 'Right to Correction',
      description: 'Update or correct inaccurate personal information',
      action: 'Update Information',
      timeline: 'Immediate'
    },
    {
      icon: 'TrashIcon',
      title: 'Right to Deletion',
      description: 'Request deletion of your personal data (subject to legal obligations)',
      action: 'Request Deletion',
      timeline: 'Within 30 days'
    },
    {
      icon: 'ArrowDownTrayIcon',
      title: 'Right to Portability',
      description: 'Receive your data in a structured, machine-readable format',
      action: 'Export Data',
      timeline: 'Within 30 days'
    },
    {
      icon: 'NoSymbolIcon',
      title: 'Right to Object',
      description: 'Object to processing of your data for marketing or profiling',
      action: 'Opt Out',
      timeline: 'Immediate'
    },
    {
      icon: 'ShieldCheckIcon',
      title: 'Right to Restrict',
      description: 'Limit how we process your data in certain circumstances',
      action: 'Request Restriction',
      timeline: 'Within 15 days'
    }
  ];

  return (
    <section id="guest-rights" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ScaleIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Your Rights</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Guest Data Rights
            </h2>
            <p className="text-lg text-text-secondary">
              Under GDPR and Indian data protection laws, you have comprehensive rights regarding your personal information.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {rights.map((right, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-card border border-border p-6 transition-all duration-300 hover:shadow-elevated"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={right.icon as any} size={24} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-text-primary mb-2">
                      {right.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-4">
                      {right.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <button className="text-primary hover:text-primary/80 text-sm font-medium transition-colors">
                        {right.action}
                      </button>
                      <span className="text-xs text-text-tertiary">{right.timeline}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onToggle}
            className="mt-8 w-full p-6 bg-white rounded-xl shadow-card border border-border hover:shadow-elevated transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="InformationCircleIcon" size={24} className="text-primary" />
                <span className="text-lg font-semibold text-text-primary">Legal Framework & Compliance</span>
              </div>
              <Icon 
                name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                size={24} 
                className="text-text-tertiary" 
              />
            </div>
            {isExpanded && (
              <div className="mt-6 pt-6 border-t border-border text-left space-y-4 animate-scale-in">
                <div>
                  <h4 className="font-semibold text-text-primary mb-2">GDPR Compliance</h4>
                  <p className="text-text-secondary text-sm">
                    We comply with the General Data Protection Regulation (GDPR) for all guests, regardless of location. This includes lawful basis for processing, data minimization, and purpose limitation principles.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary mb-2">Indian Data Protection Laws</h4>
                  <p className="text-text-secondary text-sm">
                    We adhere to the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023, ensuring compliance with Indian data protection standards and regulations.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary mb-2">Data Retention</h4>
                  <p className="text-text-secondary text-sm">
                    We retain personal data only as long as necessary for the purposes outlined in this policy or as required by law. Booking records are retained for 7 years for tax and legal compliance.
                  </p>
                </div>
              </div>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}